import { AlertCircle, Mail } from 'lucide-react'
import type { UseCompanyInfoTabReturn } from '@/features/company/types'
import styles from '../../../styles/companyAdd.module.css'
import {
    ZIPCODE_FORMAT_ERROR,
    isValidPhysicalZipcode,
} from '@/features/company/utils/companyInfoTabLogic'
import { SectionCard } from '@/components/ui/SectionCard'

export function CompanyInfoMailingAddressSection({
        register,
        errors,
        states,
        canEditInfo,
        watchIsMailingAddress,
}: UseCompanyInfoTabReturn) {
    return (
        <>
            {watchIsMailingAddress && (
                <div id="mailing-address-section" data-testid="company-info-mailing-address-section">
                    <SectionCard icon={Mail} title="Mailing Address">
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Address Line1<span className={styles.requiredStar}>*</span></label>
                                    <textarea
                                        rows={2}
                                        disabled={!canEditInfo}
                                        {...register("mailing_address1", {
                                            validate: (v, fv) =>
                                                !fv.is_mailing_address || (v && String(v).trim())
                                                    ? true
                                                    : "Mailing Address Line1 is required.",
                                        })}
                                        data-testid="company-info-mailing-address-line1-textarea"
                                        className={`${styles.customFormControl} ${errors.mailing_address1 ? "!border-red-500" : ""}`}
                                    />
                                    {errors.mailing_address1 && (
                                        <div className={styles.errorMessage}>
                                            <AlertCircle className="h-4 w-4" /> {errors.mailing_address1.message as string}
                                        </div>
                                    )}
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Address Line2</label>
                                    <textarea
                                        rows={2}
                                        disabled={!canEditInfo}
                                        {...register("mailing_address2")}
                                        data-testid="company-info-mailing-address-line2-textarea"
                                        className={styles.customFormControl}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>City<span className={styles.requiredStar}>*</span></label>
                                    <input
                                        type="text"
                                        disabled={!canEditInfo}
                                        {...register("mailing_city", {
                                            validate: (v, fv) =>
                                                !fv.is_mailing_address || (v && String(v).trim())
                                                    ? true
                                                    : "Mailing City is required.",
                                        })}
                                        data-testid="company-info-mailing-city-input"
                                        className={`${styles.customFormControl} ${errors.mailing_city ? "!border-red-500" : ""}`}
                                    />
                                    {errors.mailing_city && (
                                        <div className={styles.errorMessage}>
                                            <AlertCircle className="h-4 w-4" /> {errors.mailing_city.message as string}
                                        </div>
                                    )}
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Select State<span className={styles.requiredStar}>*</span></label>
                                    <div className={styles.selectWrap}>
                                        <select
                                            disabled={!canEditInfo}
                                            className={`${styles.customFormControl} ${errors.mailing_state ? "!border-red-500" : ""}`}
                                            {...register("mailing_state", {
                                                validate: (v, fv) =>
                                                    !fv.is_mailing_address || (v && String(v).trim())
                                                        ? true
                                                        : "Mailing State is required.",
                                            })}
                                            data-testid="company-info-mailing-state-select"
                                        >
                                            <option value="">Select State</option>
                                            {states.map((s) => (
                                                <option key={s.state_code} value={s.state_code}>{s.state_name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {errors.mailing_state && (
                                        <div className={styles.errorMessage}>
                                            <AlertCircle className="h-4 w-4" /> {errors.mailing_state.message as string}
                                        </div>
                                    )}
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Zipcode<span className={styles.requiredStar}>*</span></label>
                                    <input
                                        type="text"
                                        disabled={!canEditInfo}
                                        {...register("mailing_zipcode", {
                                            validate: (v, fv) => {
                                                if (!fv.is_mailing_address) return true;
                                                const z = (v && String(v).trim()) || "";
                                                if (!z) return "Mailing Zipcode is required.";
                                                return isValidPhysicalZipcode(z) || ZIPCODE_FORMAT_ERROR;
                                            },
                                        })}
                                        data-testid="company-info-mailing-zipcode-input"
                                        className={`${styles.customFormControl} ${errors.mailing_zipcode ? "!border-red-500" : ""}`}
                                    />
                                    {errors.mailing_zipcode && (
                                        <div className={styles.errorMessage}>
                                            <AlertCircle className="h-4 w-4" /> {errors.mailing_zipcode.message as string}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </SectionCard>
                </div>
            )}
        </>
    )
}
